import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContractItem } from '@core/models/contract-item.model';
import { ContractUser } from '@core/models/contract-user.model';
import { ContractUt } from '@core/models/contract-ut.model';
import { ContractUtService } from '@core/services/contract-ut/contract-ut.service';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
standalone: false,
  selector: 'app-contract-ut-informations',
  templateUrl: './contract-ut-informations.component.html',
  styleUrls: ['./contract-ut-informations.component.scss'],
})
export class ContractUtInformationsComponent implements OnInit {
  @Input() contractItem: ContractItem;
  @Input() contractUserList: ContractUser[];

  contractUtFormGroup: FormGroup;

  // Autocomplete data
  isContractUtLoading = false;

  contractUtList: ContractUt[] = [];
  filteredContractUtList: Observable<ContractUt[]>;

  componentDestroyed$: Subject<void> = new Subject();

  constructor(private fb: FormBuilder, private contractUtService: ContractUtService) {}

  ngOnInit(): void {
    this.initForm();
    this.getUsersAccessToContractList();
  }

  public cancelFormsModification() {
    this.initForm();
    this.contractUserList = [...this.contractItem.contractUserList];
  }

  public isFormValid(): boolean {
    return this.contractUserList.length > 0;
  }

  public getFormData() {
    return this.contractUserList;
  }

  public markAllAsTouched() {
    this.contractUtFormGroup.markAllAsTouched();
  }

  protected initForm() {
    this.contractUtFormGroup = this.fb.group({
      ut: [null, Validators.required],
    });
  }

  // --- contract user & customer service manager ---

  private getUsersAccessToContractList() {
    this.isContractUtLoading = true;

    this.contractUtService
      .getList({ utType: 'usersAccessToContract' })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        contractUtList => {
          this.isContractUtLoading = false;

          this.contractUtList = contractUtList;

          this.initFilteredContractUtList();
        },
        error => {
          this.isContractUtLoading = false;
        }
      );
  }

  private initFilteredContractUtList() {
    if (!this.isContractUtLoading) {
      this.filteredContractUtList = this.contractUtFormGroup.get('ut').valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => this._getLabel(value)),
        map(label => (label ? this._filterContractUt(label) : this.contractUtList.slice()))
      );
    }
  }

  private _filterContractUt(value: string = ''): ContractUt[] {
    const filterValue = value.toLowerCase();

    return this.contractUtList.filter(
      contractUt => contractUt.id.toString().toLowerCase().includes(filterValue) || contractUt.name.toLowerCase().includes(filterValue)
    );
  }

  public addUtToList(ut: ContractUt) {
    if (!ut || this.contractUserList.find(cu => cu.user.id === ut.id)) {
      return;
    }

    this.contractUserList.push(
      new ContractUser({
        id: null,
        contractId: this.contractItem.id,
        user: ut,
        isActive: 1,
        createdAt: new Date(),
        updatedAt: null,
      })
    );

    this.contractUtFormGroup.get('ut').setValue('');
    this.initFilteredContractUtList();
  }

  // --- Utility ---

  private _getLabel(value: string | ContractUt): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  public formatDisplayNameWithId(data: any): string {
    return data?.name + ' (' + data?.id + ')';
  }

  // --- Utility ---

  public displayAutoCompleteData(data) {
    return data?.name && data?.id ? `${data.name} (${data.id})` : data;
  }

  public removeUt(item) {
    this.contractUserList = this.contractUserList.filter(u => u !== item);
  }

  // --- On Destroy ---

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
